import { useId, useState } from "react";

import {
  AppDialog,
  AppDialogActions,
  DialogActionsSpacer,
  DialogPrimaryButton,
  DialogSecondaryButton,
  DialogTertiaryButton,
} from "@/components/ui/app-dialog";
import { focusPaper } from "@/components/ui/dialog-focus";
import { RichTextField } from "@/components/ui/rich-text-field";

// The donor's note form — `NotesDialogForm` (`components/specSheets/
// NotesDialogForm.tsx`), shared in the source beside the name-and-note form, so
// shared here (`docs/agents/migration-guidelines.md`, "Shared donor components
// port as shared Atlas components"). The Editor's per-location notes table
// opens it for a room's note on a line (#808).
//
// **The frame**: the donor's shared `Dialog`, `fullWidth` at MUI's default
// `sm` — 600px — titled `Edit note` or `Add a note` by `edit`, with **no focus
// handed back on the close** (`disableRestoreFocus`). It passes no `onEntered`
// focus handler (unlike `GenericNameNoteDialogForm`), so MUI's trap focuses the
// paper and nothing else. Escape, the backdrop and the X close it even
// mid-save, as the donor passes `onClose` straight through.
//
// **The form**: one rich-text `Note`, no length rule.
//
// **The actions**: `Clear Note` (tertiary, never held — it empties the box and
// submits nothing), a spacer, `Cancel` and the submit (`Save` / `Add note`),
// both **held** while `saving` — the donor's own `isSaving` gate, which
// disables them and draws no spinner.
//
// **The value** is the dialog's own, as the donor's caller-held `useForm` is:
// written in whenever `seed` changes — by value, the donor's
// `useEffect([currentNotes])` with `setValue` — and emptied whenever the dialog
// is closed and not `edit` (the donor's `!edit && !open` effect).

export interface NotesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The room has a note: `Edit note` / `Save`, else `Add a note` / `Add note`. */
  edit: boolean;
  /** The note the box shows — written in whenever this value changes. */
  seed: string;
  /** The write is in flight: Cancel and the submit are held. */
  saving?: boolean;
  /** The note as typed — `""` after `Clear Note`. */
  onSubmit: (notes: string) => void;
  /** The paper has gone — the donor's `onExited`. */
  onExited?: () => void;
}

export function NotesDialog({
  open,
  onOpenChange,
  edit,
  seed,
  saving = false,
  onSubmit,
  onExited,
}: NotesDialogProps) {
  const formId = useId();
  const [notes, setNotes] = useState(seed);

  // `useEffect(() => setValue('notes', currentNotes), [currentNotes])`, as a
  // render-time adjustment.
  const [seeded, setSeeded] = useState(seed);
  if (seeded !== seed) {
    setSeeded(seed);
    setNotes(seed);
  }

  // `useEffect(() => { if (!edit && !open) resetNotesField() }, [edit, open])`
  // — on the change that closes a room without a note, as a render-time
  // adjustment like the seed's.
  const resets = !edit && !open;
  const [reset, setReset] = useState(resets);
  if (reset !== resets) {
    setReset(resets);
    if (resets) setNotes("");
  }

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title={edit ? "Edit note" : "Add a note"}
      size="sm"
      onOpenAutoFocus={focusPaper}
      restoreFocus={false}
      onCloseAutoFocus={() => onExited?.()}
      actions={
        <AppDialogActions>
          <DialogTertiaryButton onClick={() => setNotes("")}>Clear Note</DialogTertiaryButton>
          <DialogActionsSpacer />
          <DialogSecondaryButton disabled={saving} onClick={() => onOpenChange(false)}>
            Cancel
          </DialogSecondaryButton>
          <DialogPrimaryButton type="submit" form={formId} disabled={saving}>
            {edit ? "Save" : "Add note"}
          </DialogPrimaryButton>
        </AppDialogActions>
      }
    >
      <form
        id={formId}
        noValidate
        autoComplete="off"
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (saving) return;
          onSubmit(notes);
        }}
      >
        <RichTextField label="Note" value={notes} onChange={setNotes} />
      </form>
    </AppDialog>
  );
}
